// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import "./App.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Provider } from "react-redux";
// @ts-ignore
import { AuthProvider } from "@asgardeo/auth-react";
import { AsgardioConfig } from "./config/config";
import { store } from "./slices/store";
import { SnackbarProvider } from "notistack";

import AppAuthProvider from "./context/AuthContext";
import AuthHandler from "./utils/AuthHandler";

const theme = createTheme({
  palette: {
    background: {
      default: "#ffffff",
    },
    primary: {
      contrastText: "#ffffff",
      main: "#212a32",
    },
    secondary: {
      contrastText: "#ffffff",
      main: "#ff7300",
    },
    text: {
      primary: "#212a32",
      secondary: "#6b778c",
    },
    success: {
      contrastText: "#ffffff",
      main: "#05c46b",
    },
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', PlusJakarta, -apple-system, BlinkMacSystemFont, sans-serif",
    button: {
      fontWeight: 500,
    },
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '2px 5px 10px 0px rgb(0 0 0 / 20%)',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: '0.075rem',
          fontSize: 16,
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.variant === "outlined" && {
            border: "1px solid #ff7300",
            paddingLeft: "2.5rem",
            paddingRight: "2.5rem",
            borderRadius: "3px",
            fontWeight: "bold",
            letterSpacing: "0.05rem",
            fontSize: "0.9rem",
            ":hover": {
              backgroundColor: "#ff7300",
              border: "1px solid #ff7300",
              color: "#ffffff",
            },
          }),
          ...(ownerState.variant === "contained" && {
            paddingLeft: "2.5rem",
            paddingRight: "2.5rem",
            borderRadius: 4,
            fontWeight: "bold",
            letterSpacing: "0.05rem",
            fontSize: "0.9rem",
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.getContrastText(theme.palette.secondary.main),
            boxShadow: "none",
            border: "none",
            ":hover": {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.getContrastText(theme.palette.secondary.main),
            },
          }),
        }),
      },
    },
  },
});

function App() {
  return (
    <SnackbarProvider maxSnack={3} preventDuplicate>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <AuthProvider config={AsgardioConfig}>
            <AppAuthProvider>
              <AuthHandler />
            </AppAuthProvider>
          </AuthProvider>
        </Provider>
      </ThemeProvider>
    </SnackbarProvider>
  );
}

export default App;
